/**
 * Pure helper functions extracted from the analytics save / restore code paths
 * in operations.tsx.  Keeping these as standalone, side-effect-free functions
 * makes them unit-testable without mounting the full React component.
 */

/** Matches the nf() helper in operations.tsx. */
export function nf(v: string): number {
  const p = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(p) ? p : 0;
}

/**
 * Builds the fragment of the /api/op-analytics/save-inputs POST body that
 * concerns totalIncome.
 *
 * Mirrors lines 1611 and 1623 of operations.tsx:
 *   const totalIncomeAtSave = manualTotalIncome !== "" ? nf(manualTotalIncome) : null;
 *   if (totalIncomeAtSave !== null) body.totalIncome = totalIncomeAtSave;
 *
 * Returns the numeric value to store, or null when the field is empty.
 */
export function computeTotalIncomeForSave(
  manualTotalIncome: string
): number | null {
  return manualTotalIncome !== "" ? nf(manualTotalIncome) : null;
}

/**
 * Decides whether the restore useEffect should write a new value for
 * manualTotalIncome, and returns it.
 *
 * Mirrors line 1421 of operations.tsx:
 *   if (inp.totalIncome != null && !manualTotalIncome) setManualTotalIncome(String(inp.totalIncome));
 *
 * @param savedTotalIncome   Value from the persisted input (inp.totalIncome).
 * @param currentValue       The component's current manualTotalIncome state.
 * @returns The value to pass to setManualTotalIncome, or null when no update
 *          is needed.
 */
export function resolveRestoredTotalIncome(
  savedTotalIncome: number | null | undefined,
  currentValue: string
): string | null {
  if (savedTotalIncome != null && !currentValue) {
    return String(savedTotalIncome);
  }
  return null;
}

/**
 * Returns the value resetToSaved() assigns to manualTotalIncome.
 *
 * Mirrors line 1479 of operations.tsx:
 *   setManualTotalIncome(inp.totalIncome != null ? String(inp.totalIncome) : "");
 *
 * @param savedTotalIncome  Value from the persisted input (inp.totalIncome).
 */
export function resetTotalIncomeFromSaved(
  savedTotalIncome: number | null | undefined
): string {
  return savedTotalIncome != null ? String(savedTotalIncome) : "";
}
